import { useState, FormEvent } from 'react';
import { useSiteContent, stringItems } from '../hooks/useSiteContent';
import { useLanguage } from '../i18n/LanguageContext';
import { submitApplication } from '../services/forms';
import { mediaUrl } from '../services/api';
import bannerImage from '../components/assets/1.jpg';
import iconLinkedin from '../components/assets/icon-linkedin.png';
import iconKariyer from '../components/assets/icon-kariyer.png';

const PAGE_SLUG = 'kariyer';

const CHANNEL_ICONS: Record<string, string> = {
  linkedin: iconLinkedin,
  'kariyer.net': iconKariyer,
  kariyer: iconKariyer,
};

const DEFAULT_PROCESS = [
  'Online başvuru ve CV değerlendirmesi',
  'İnsan kaynakları ön görüşmesi',
  'Teknik / departman görüşmesi',
  'Referans ve belge kontrolü',
  'Teklif ve işe başlangıç',
];

const DEFAULT_CHANNELS = [
  {
    label: 'LinkedIn',
    url: 'https://linkedin.com/jobs',
    description: 'Güncel açık pozisyonlarımızı LinkedIn üzerinden takip edebilirsiniz.',
  },
  {
    label: 'Kariyer.net',
    url: 'https://kariyer.net',
    description: 'İlanlarımıza Kariyer.net üzerinden de ulaşabilir ve başvurabilirsiniz.',
  },
];

function formatCareerHeroTitle(value?: string) {
  const text = (value || '').trim();
  if (!text) {
    return '<span>Güçlü Bir Gelecek İçin</span><br />Aynı Rotadayız';
  }
  if (/[<>]/.test(text)) return text;

  const parts = text
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return `<span>${parts[0]}</span><br />${parts.slice(1).join(' ')}`;
  }

  const words = text.split(/\s+/);
  if (words.length > 3) {
    const firstLine = words.slice(0, Math.ceil(words.length / 2)).join(' ');
    const secondLine = words.slice(Math.ceil(words.length / 2)).join(' ');
    return `<span>${firstLine}</span><br />${secondLine}`;
  }

  return `<span>${text}</span>`;
}

function formatChannelsTitle(value: string) {
  const text = value.trim();
  const parts = text.split(/\s+/);
  if (parts.length < 2) {
    return { firstLine: text, accent: '' };
  }

  return {
    firstLine: parts.slice(0, -1).join(' '),
    accent: parts[parts.length - 1],
  };
}

function formatProcessTitle(value?: string) {
  const text = (value || 'İşe Alım Sürecimiz').trim();
  const words = text.split(/\s+/);
  if (words.length < 2) return { accent: text, rest: '' };
  return {
    accent: words.slice(0, 2).join(' '),
    rest: words.slice(2).join(' '),
  };
}

function formatApplyTitle(value: string) {
  const text = value.trim();
  const words = text.split(/\s+/);
  if (words.length < 2) return { accent: text, rest: '' };
  return {
    accent: words[0],
    rest: words.slice(1).join(' '),
  };
}

export function CareersPage() {
  const { getPage, getBlock, content } = useSiteContent();
  const { t, tList } = useLanguage();
  const page = getPage(PAGE_SLUG);
  const jobLinks = getBlock(PAGE_SLUG, 'job_links');
  const process = getBlock(PAGE_SLUG, 'recruitment_process');
  const linkItems = stringItems(jobLinks?.items);
  const processItems = stringItems(process?.items);
  const jobs = (content.jobs || []).filter((job) => job.isPublished !== false);
  const settings = content.settings;

  const channels =
    linkItems.length > 0
      ? linkItems.map((raw) => {
          const [label = '', url = '#'] = raw.split('|');
          const known = DEFAULT_CHANNELS.find(
            (item) => item.label.toLocaleLowerCase('tr-TR') === label.toLocaleLowerCase('tr-TR'),
          );
          return {
            label: label || 'Başvuru Kanalı',
            url: url || '#',
            description: known?.description || 'Açık pozisyonlarımızı buradan inceleyebilirsiniz.',
          };
        })
      : DEFAULT_CHANNELS;

  const steps = tList(processItems.length > 0 ? processItems : DEFAULT_PROCESS);
  const heroImage = mediaUrl(page?.heroImage) || bannerImage;
  const channelsTitle = formatChannelsTitle(t('Açık pozisyonlarımızı nereden görebilirsiniz?'));
  const processTitle = formatProcessTitle(t(process?.title || 'İşe Alım Sürecimiz'));
  const applyTitle = formatApplyTitle(t("CV'nizi bize ulaştırın"));

  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const data = new FormData(formEl);
    const cv = data.get('cv');

    if (!(cv instanceof File) || cv.size === 0) {
      setStatus('error');
      setError(t('CV dosyası zorunludur.'));
      return;
    }

    setStatus('sending');
    setError('');
    try {
      await submitApplication(data);
      setStatus('success');
      formEl.reset();
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : t('Başvuru gönderilemedi.'));
    }
  }

  return (
    <>
      <section
        className="career-hero"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="career-hero-overlay" />
        <div className="container career-hero-inner">
          <div className="career-hero-eyebrow">{t('Ticaret Dürüstlük Üzerine Kuruludur')}</div>

          <h1
            className="career-hero-title"
            dangerouslySetInnerHTML={{
              __html: t(formatCareerHeroTitle(page?.heroTitle)),
            }}
          />
          <p className="career-hero-desc">
            {t(
              page?.heroSubtitle ||
                'TBH Port Adana olarak büyüyen ekibimize katılmak isteyen adaylara açık pozisyonlar ve genel başvuru imkanı sunuyoruz. SANKO Holding güvencesiyle kariyerinizi birlikte inşa edelim.',
            )}
          </p>

          <div className="career-hero-highlights">
            <div>
              <strong>SANKO</strong>
              <span>{t('Holding Güvencesi')}</span>
            </div>
            <div>
              <strong>{t('Yan Haklar')}</strong>
              <span>{t('Rekabetçi Yan Haklar')}</span>
            </div>
          </div>

          <div className="career-hero-actions">
            <a href="#acik-pozisyonlar" className="btn btn-red">
              {t('AÇIK POZİSYONLAR')}
            </a>
            <a href="#genel-basvuru" className="btn btn-outline-light">
              {t('GENEL BAŞVURU YAP')}
            </a>
          </div>
        </div>
      </section>

      <section className="career-channels-section" id="basvuru-kanallari">
        <div className="container">
          <div className="career-channels-eyebrow">{t('BAŞVURU KANALLARI')}</div>
          <h2 className="career-channels-title">
            {channelsTitle.firstLine}
            <span>{channelsTitle.accent}</span>
          </h2>
          <div className="career-channels-grid">
            {channels.map((channel) => (
              <a
                className="career-channel-card"
                href={channel.url}
                target="_blank"
                rel="noreferrer"
                key={channel.label}
              >
                <div className="career-channel-head">
                  <div className="career-channel-icon" aria-hidden="true">
                    <img
                      src={
                        Object.entries(CHANNEL_ICONS).find(([key]) =>
                          channel.label.toLocaleLowerCase('tr-TR').includes(key),
                        )?.[1] || iconKariyer
                      }
                      alt=""
                    />
                  </div>
                  <div className="career-channel-badge">{t('AÇIK POZİSYONLAR')}</div>
                </div>
                <h3>{channel.label}</h3>
                <p>{t(channel.description)}</p>
                <div className="career-channel-divider" />
                <span className="career-channel-cta">{t('Açık Pozisyonlar')}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="career-jobs-section" id="acik-pozisyonlar">
        <div className="container">
          {jobs.length > 0 && (
            <div className="career-jobs-grid">
              {jobs.map((job) => (
                <article className="career-job-card" key={job.id}>
                  <div className="career-job-meta">
                    {job.department && <span>{t(job.department)}</span>}
                    {job.location && <span>{t(job.location)}</span>}
                    {job.employmentType && <span>{t(job.employmentType)}</span>}
                  </div>
                  <h3>{t(job.title)}</h3>
                  {job.description && <p>{t(job.description)}</p>}
                  {job.requirements && (
                    <p className="career-job-requirements">{t(job.requirements)}</p>
                  )}
                  {job.applyUrl ? (
                    <a
                      className="btn btn-navy"
                      href={job.applyUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('Başvur →')}
                    </a>
                  ) : (
                    <a className="btn btn-navy" href="#genel-basvuru">
                      {t('Genel Başvuru Yap →')}
                    </a>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="career-process-section">
        <div className="container career-process-layout">
          <div className="career-process-copy">
            <div className="career-process-badge">{t('SÜREÇ')}</div>
            <h2 className="career-process-title">
              <span>{processTitle.accent}</span>
              {processTitle.rest && <> {processTitle.rest}</>}
            </h2>
            <p>{t('Adım adım, şeffaf ve öngörülebilir bir aday deneyimi.')}</p>
          </div>
          <ol className="career-process-list">
            {steps.map((step, index) => (
              <li key={`${index}-${step}`}>
                <span className="career-process-dot" aria-hidden="true" />
                <p>{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="career-apply-section section-gray" id="genel-basvuru">
        <div className="container career-apply-layout">
          <div className="career-apply-copy">
            <div className="career-apply-eyebrow">{t('BAŞVURU')}</div>
            <h2 className="career-apply-title">
              <span>{applyTitle.accent}</span>
              {applyTitle.rest && <> {applyTitle.rest}</>}
            </h2>
            <p className="career-apply-description">
              {t(
                'Açık pozisyona başvurmak veya genel kariyer havuzumuza katılmak için aşağıdaki formu doldurun. İK ekibimiz başvurunuzu en geç 10 iş günü içinde değerlendirip size geri dönüş yapacak.',
              )}
            </p>
            <ul className="career-apply-contacts">
              <li>
                <strong>{t('İK e-posta:')}</strong>
                <a href={`mailto:${settings.footerEmail || 'ik@tbhport.com'}`}>{settings.footerEmail || 'ik@tbhport.com'}</a>
              </li>
              <li>
                <strong>{t('Adres:')}</strong>
                <span>{t(settings.footerAddress || 'Sugözü, 01682 Yumurtalık / Adana')}</span>
              </li>
              <li>
                <strong>LinkedIn:</strong>
                <a
                  href={
                    settings.socialLinks?.find((item) =>
                      // tr-TR kucultmesi "LinkedIn"i "linkedın" yapar; eslesme icin toLowerCase
                      item.platform.toLowerCase().includes('linkedin'),
                    )?.url || 'https://linkedin.com'
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  TBH Port Adana
                </a>
              </li>
            </ul>
          </div>

          <form className="apply career-apply-form" onSubmit={handleSubmit}>
            {status === 'success' ? (
              <p className="form-note">{t('Başvurunuz alındı. İlginiz için teşekkür ederiz.')}</p>
            ) : (
              <>
                {status === 'error' && <p className="form-error">{t(error)}</p>}
                <div className="row">
                  <div>
                    <label htmlFor="career-firstName">{t('Ad')} *</label>
                    <input id="career-firstName" name="firstName" required />
                  </div>
                  <div>
                    <label htmlFor="career-lastName">{t('Soyad')} *</label>
                    <input id="career-lastName" name="lastName" required />
                  </div>
                </div>
                <div className="row">
                  <div>
                    <label htmlFor="career-email">{t('E-posta')} *</label>
                    <input id="career-email" type="email" name="email" required />
                  </div>
                  <div>
                    <label htmlFor="career-phone">{t('Telefon')} *</label>
                    <input id="career-phone" name="phone" required />
                  </div>
                </div>
                <div className="row">
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label htmlFor="career-cv">CV (PDF / DOCX) *</label>
                    <input id="career-cv" type="file" name="cv" accept=".pdf,.doc,.docx" required />
                    <small className="career-file-hint">
                      {t('Dosya Seçin veya Buraya Sürükleyin (Max 5mb)')}
                    </small>
                  </div>
                </div>
                <div className="row">
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label htmlFor="career-message">{t('Mesajınız...')}</label>
                    <textarea id="career-message" name="message" rows={4} />
                  </div>
                </div>
                <label className="career-kvkk">
                  <input type="checkbox" required />
                  <span>
                    {t(
                      "KVKK Aydınlatma Metni'ni okudum ve kişisel verilerimin başvuru sürecinde işlenmesini kabul ediyorum.",
                    )}
                  </span>
                </label>
                <button className="btn btn-navy" type="submit" disabled={status === 'sending'}>
                  {status === 'sending' ? t('Gönderiliyor...') : t('Başvuru Gönder')}
                </button>
              </>
            )}
          </form>
        </div>
      </section>
    </>
  );
}
